import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Article,
  Articles,
  emptyArticleMeta,
  getIllustrationPath,
} from "./articleHelpers";
import { uploadJPG } from "./fileUploadHelper";
import ArticleMetaFields from "./ArticleMetaFields";
import HTMLEditor from "./HTMLEditor";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import { API } from "aws-amplify";
import config from "./config";

const ArticleEditor: React.FC<{
  articles: Articles;
  dispatch: React.Dispatch<{
    idToUpdate: string;
    articleToUpdate: Article;
  }>;
}> = ({ articles, dispatch }) => {
  const [articleMeta, setArticleMeta] = useState<Article>();
  const [content, setContent] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [illustrationPath, setIllustrationPath] = useState<string | null>();
  const { articleId } = useParams();

  const illustrationCallback = async (file: string | ArrayBuffer | null) => {
    if (articleId) {
      setIsUploading(true);
      try {
        const data = await API.post(config.apiName, "/addIllustration", {
          body: {
            article: { id: articleId, ...articleMeta, content },
            illustration: file,
          },
        });
        setArticleMeta(data);
        if (data.illustrationHash) {
          setTimeout(
            () =>
              setIllustrationPath(
                getIllustrationPath(articleId, data.illustrationHash)
              ),
            config.imageTemporisation
          );
        }
      } finally {
        setIsUploading(false);
      }
    }
  };

  const illustrationError = () => {
    setIsUploading(false);
  };

  const uploadIllustration = (file: File) => {
    uploadJPG(file, illustrationCallback, illustrationError);
  };

  const updateDatabase = async (toDeploy?: boolean) => {
    try {
      setIsUploading(true);
      const data = await API.post(config.apiName, "/addArticle", {
        body: {
          id: articleId,
          ...articleMeta,
          content,
          toDeploy,
        },
      });
      setArticleMeta(data);
      setIsUploading(false);
    } catch (error) {
      alert(JSON.stringify(error) ?? error);
      setIsUploading(false);
    }
  };

  const notEmpty = () =>
    content || articleMeta?.slug || articleMeta?.title || articleMeta?.subTitle;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    document.title = "Éditeur d'article";
    if (articleId) {
      const initialArticle = articleId in articles ? articles[articleId] : null;
      setArticleMeta(
        initialArticle ?? { ...emptyArticleMeta, createdAt: Date.now() }
      );
      setContent(initialArticle ? initialArticle?.content ?? "" : "");
      if (initialArticle?.illustrationHash) {
        setIllustrationPath(
          getIllustrationPath(articleId, initialArticle.illustrationHash)
        );
      }
    }
  }, [articleId]);

  useEffect(() => {
    if (articleId && notEmpty() && !isUploading) {
      dispatch({
        idToUpdate: articleId,
        articleToUpdate: { ...articleMeta, content },
      });
    }
  }, [articleId, content, articleMeta]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (notEmpty() && !isUploading) {
        updateDatabase();
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  /* eslint-enable */

  if (!articleMeta || !articleId) {
    return <></>;
  }

  return (
    <>
      <ArticleMetaFields
        articleMeta={articleMeta}
        setArticleMeta={setArticleMeta}
        isUploading={isUploading}
        uploadIllustration={uploadIllustration}
        illustrationPath={illustrationPath}
      />
      <HTMLEditor content={content} setContent={setContent} />
      <Box sx={{ my: 3 }}>
        <LoadingButton
          loading={isUploading}
          disabled={!notEmpty() || isUploading}
          variant="outlined"
          onClick={async () => updateDatabase()}
        >
          Sauvegarder le brouillon
        </LoadingButton>
        <LoadingButton
          loading={isUploading}
          disabled={!articleMeta.blog || isUploading || !articleMeta.slug}
          variant="contained"
          sx={{ ml: 1 }}
          onClick={async () => {
            updateDatabase(true);
          }}
        >
          Déployer sur le blog
        </LoadingButton>
      </Box>
    </>
  );
};

export default ArticleEditor;
